import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Box, Paper, Typography, Chip, IconButton, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,
  Stack, Alert, CircularProgress, Switch, FormControlLabel, Checkbox,
  List, ListItem, ListItemButton, ListItemText, Tabs, Tab, Table, TableHead,
  TableBody, TableRow, TableCell, Grid, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SyncAlt, Edit, Add, FactCheck, CleaningServices, MenuBook } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import {
  API_BASE_URL, MAPEO_PRECIOS_LISTAR, MAPEO_PRECIOS_ALTA, MAPEO_PRECIOS_ACTUALIZAR, MAPEO_PRECIOS_CONTRA_EXCEL,
  MAPEO_PRECIOS_DUPLICADOS_PREVIEW, MAPEO_PRECIOS_DUPLICADOS_APLICAR, PRODUCTOS_LISTAR,
  PRECIOS_PREVISUALIZAR, PRODUCTO_FORMDATA, PRODUCTO_ALTA,
} from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import GuiaActualizarPrecios from './GuiaActualizarPrecios.jsx';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mismo criterio que xlsxNormalizarNombre() en el backend, para poder
// comparar en el navegador sin ida y vuelta al servidor.
const normalizarNombre = (s) => String(s || '').trim().replace(/\s+/g, ' ').toUpperCase();

const FORM_VACIO = { nombreExcelOriginal: null, nombre_excel: '', categoria: '', activo: 1, productos: [] };

// Misma clave que CLAVE_ESTADO en PreciosImportar.jsx — es cómo esta pantalla
// se entera de "hay un Excel recién subido" sin que haga falta pasarlo por
// navegación ni resubirlo: si está, se usa para traer la planilla y mostrarla
// como vista por defecto (ver más abajo).
const CLAVE_ESTADO_PRECIOS = 'precios_import_pendiente';
const leerExcelPendiente = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem(CLAVE_ESTADO_PRECIOS) || 'null');
    return s?.token && s?.hoja ? { token: s.token, hoja: s.hoja } : null;
  } catch { return null; }
};

function MapeoPrecios() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filas, setFilas] = useState([]);
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  // Pestaña "Planilla del Excel": por defecto si se llega con un Excel
  // recién subido en PreciosImportar.jsx, si no la pantalla arranca en
  // "Todos los mapeos" (comportamiento de siempre).
  const excelPendienteInicial = useRef(leerExcelPendiente()).current;
  const [tab, setTab] = useState(excelPendienteInicial ? 0 : 1);
  const [excelToken] = useState(excelPendienteInicial?.token || null);
  const [excelHoja] = useState(excelPendienteInicial?.hoja || null);
  const [cargandoPlanilla, setCargandoPlanilla] = useState(!!excelPendienteInicial);
  const [errorPlanilla, setErrorPlanilla] = useState('');
  const [filasExcel, setFilasExcel] = useState([]);

  // Picker de mapeo inline para una fila roja de la planilla.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFila, setPickerFila] = useState(null);
  const [pickerSeleccion, setPickerSeleccion] = useState([]);
  const [pickerBusqueda, setPickerBusqueda] = useState('');
  const [pickerGuardando, setPickerGuardando] = useState(false);
  const [pickerError, setPickerError] = useState('');

  // "Crear producto nuevo" dentro del picker, para cuando el producto del
  // Excel todavía no existe en el catálogo.
  const [crearProductoAbierto, setCrearProductoAbierto] = useState(false);
  const [gruposProducto, setGruposProducto] = useState([]);
  const [nuevoProducto, setNuevoProducto] = useState({ descripcion: '', precio: '', grupo: '', id_club_grido: '' });
  const [creandoProducto, setCreandoProducto] = useState(false);
  const [errorNuevoProducto, setErrorNuevoProducto] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [formInicial, setFormInicial] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [busquedaProductos, setBusquedaProductos] = useState('');
  const [confirmarDescarteOpen, setConfirmarDescarteOpen] = useState(false);
  const [guiaAbierta, setGuiaAbierta] = useState(false);

  // Verificación contra un Excel real: qué nombres de mapeo ya no
  // corresponden a ninguna línea de la lista de precios actual.
  const fileVerificarRef = useRef(null);
  const [verificando, setVerificando] = useState(false);
  const [nombresEnExcel, setNombresEnExcel] = useState(null); // Set<string> normalizado, o null si no se verificó
  const [archivoVerificado, setArchivoVerificado] = useState('');
  const [soloNoEncontrados, setSoloNoEncontrados] = useState(false);

  // Limpieza de duplicados: un producto que quedó activo en un mapeo
  // individual viejo Y en un grupo más grande (ej. "SMOOTHIE").
  const [duplicadosOpen, setDuplicadosOpen] = useState(false);
  const [buscandoDuplicados, setBuscandoDuplicados] = useState(false);
  const [duplicados, setDuplicados] = useState(null);
  const [limpiando, setLimpiando] = useState(false);

  const buscarDuplicados = async () => {
    setDuplicadosOpen(true);
    setBuscandoDuplicados(true);
    setDuplicados(null);
    try {
      const res = await axios.get(API_BASE_URL + MAPEO_PRECIOS_DUPLICADOS_PREVIEW, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al buscar duplicados.');
      setDuplicados(res.data.data?.conflictos || []);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al buscar duplicados.');
      setDuplicadosOpen(false);
    } finally {
      setBuscandoDuplicados(false);
    }
  };

  const limpiarDuplicados = async () => {
    setLimpiando(true);
    try {
      const res = await axios.post(API_BASE_URL + MAPEO_PRECIOS_DUPLICADOS_APLICAR, {}, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al limpiar.');
      avisarExito(res.data.message || 'Mapeos duplicados desactivados.');
      setDuplicadosOpen(false);
      cargar();
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al limpiar.');
    } finally {
      setLimpiando(false);
    }
  };

  const cargar = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + MAPEO_PRECIOS_LISTAR, { headers: authHeader() })
      .then((res) => setFilas(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((e) => setError(e.response?.data?.message || 'Error al cargar el mapeo.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarProductosCatalogo = useCallback(() => {
    return axios.get(API_BASE_URL + PRODUCTOS_LISTAR, { headers: authHeader() })
      .then((res) => setProductosCatalogo(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => { cargarProductosCatalogo(); }, [cargarProductosCatalogo]);

  // Relee el Excel temporal (mismo archivo que subió PreciosImportar.jsx,
  // vive ~2hs en el servidor) para armar la vista "planilla" línea por línea.
  const cargarPlanilla = useCallback(async (tok, hoja) => {
    setCargandoPlanilla(true);
    setErrorPlanilla('');
    try {
      const res = await axios.get(API_BASE_URL + PRECIOS_PREVISUALIZAR, {
        params: { token: tok, sheet: hoja }, headers: authHeader(),
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al leer la hoja.');
      setFilasExcel(Array.isArray(res.data.data?.filas) ? res.data.data.filas : []);
    } catch (e) {
      setErrorPlanilla(e.response?.data?.message || e.message || 'Error al leer la hoja.');
      setFilasExcel([]);
    } finally {
      setCargandoPlanilla(false);
    }
  }, []);

  useEffect(() => {
    if (excelToken && excelHoja) cargarPlanilla(excelToken, excelHoja);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un nombre de Excel agrupa una o varias filas (una por producto): "SMOOTHIE"
  // puede representar todos los sabores cargados, no uno solo.
  const grupos = useMemo(() => {
    const mapa = new Map();
    filas.forEach((f) => {
      if (!mapa.has(f.nombre_excel)) {
        mapa.set(f.nombre_excel, { nombre_excel: f.nombre_excel, categoria: f.categoria, activo: f.activo, productos: [] });
      }
      mapa.get(f.nombre_excel).productos.push({
        id_club_grido: Number(f.id_club_grido),
        descripcion: f.producto_descripcion || null,
        precio: f.producto_precio,
      });
    });
    return Array.from(mapa.values());
  }, [filas]);

  // Opciones del buscador de productos: catálogo activo, sin duplicados por
  // id_club_grido (puede haber más de un producto con el mismo club id) +
  // cualquier id ya mapeado que no resuelva a un producto activo, para no
  // perderlo al editar un grupo existente (se ve marcado como huérfano).
  const productosOptions = useMemo(() => {
    const mapa = new Map();
    productosCatalogo.filter((p) => Number(p.activo) === 1).forEach((p) => {
      const idc = Number(p.id_club_grido);
      if (idc > 0 && !mapa.has(idc)) {
        mapa.set(idc, { id_club_grido: idc, descripcion: p.art_descripcion, precio: p.art_precio_normal });
      }
    });
    filas.forEach((f) => {
      const idc = Number(f.id_club_grido);
      if (idc > 0 && !mapa.has(idc)) mapa.set(idc, { id_club_grido: idc, descripcion: null, precio: null });
    });
    return Array.from(mapa.values()).sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || ''));
  }, [productosCatalogo, filas]);

  // enExcel: null mientras no se verificó nada; true/false una vez que sí.
  const gruposConVerificacion = useMemo(() => {
    if (!nombresEnExcel) return grupos.map((g) => ({ ...g, enExcel: null }));
    return grupos.map((g) => ({ ...g, enExcel: nombresEnExcel.has(normalizarNombre(g.nombre_excel)) }));
  }, [grupos, nombresEnExcel]);

  const filtrados = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    let base = gruposConVerificacion;
    if (soloNoEncontrados && nombresEnExcel) base = base.filter((g) => !g.enExcel);
    if (!t) return base;
    return base.filter((g) =>
      g.nombre_excel.toLowerCase().includes(t)
      || String(g.categoria || '').toLowerCase().includes(t)
      || g.productos.some((p) => String(p.descripcion || '').toLowerCase().includes(t))
    );
  }, [gruposConVerificacion, busqueda, soloNoEncontrados, nombresEnExcel]);

  const onSubirParaVerificar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerificando(true);
    setError('');
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const res = await axios.post(API_BASE_URL + MAPEO_PRECIOS_CONTRA_EXCEL, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al leer el archivo.');
      setNombresEnExcel(new Set(res.data.data?.nombres || []));
      setArchivoVerificado(file.name);
    } catch (err) {
      avisarError(err.response?.data?.message || err.message || 'Error al leer el archivo.');
    } finally {
      setVerificando(false);
      if (fileVerificarRef.current) fileVerificarRef.current.value = '';
    }
  };

  const etiquetaProducto = (p) => p.descripcion
    ? `${p.descripcion} (club ${p.id_club_grido})`
    : `Sin producto (club ${p.id_club_grido})`;

  // Lista siempre visible (no un combo que se abre/cierra, y no hace falta
  // escribir para ver algo): se ve el catálogo de entrada y se va filtrando
  // a medida que se escribe, clickeando cada checkbox seguido sin perder la
  // búsqueda.
  const productosFiltrados = useMemo(() => {
    const t = busquedaProductos.trim().toLowerCase();
    const lista = t ? productosOptions.filter((p) => etiquetaProducto(p).toLowerCase().includes(t)) : productosOptions;
    return lista.slice(0, 50);
  }, [productosOptions, busquedaProductos]);

  const toggleProducto = (p) => {
    setForm((f) => {
      const yaEsta = f.productos.some((x) => x.id_club_grido === p.id_club_grido);
      return {
        ...f,
        productos: yaEsta
          ? f.productos.filter((x) => x.id_club_grido !== p.id_club_grido)
          : [...f.productos, p],
      };
    });
  };
  const quitarProducto = (p) => setForm((f) => ({ ...f, productos: f.productos.filter((x) => x.id_club_grido !== p.id_club_grido) }));

  // --- Picker de mapeo inline (filas rojas de la planilla del Excel) ---

  const pickerFiltrados = useMemo(() => {
    const t = pickerBusqueda.trim().toLowerCase();
    const lista = t ? productosOptions.filter((p) => etiquetaProducto(p).toLowerCase().includes(t)) : productosOptions;
    return lista.slice(0, 50);
  }, [productosOptions, pickerBusqueda]);

  const togglePickerProducto = (p) => {
    setPickerSeleccion((sel) => {
      const yaEsta = sel.some((x) => x.id_club_grido === p.id_club_grido);
      return yaEsta ? sel.filter((x) => x.id_club_grido !== p.id_club_grido) : [...sel, p];
    });
  };

  const abrirPicker = (fila) => {
    setPickerFila(fila);
    // Precarga lo que ya estuviera bien resuelto (fan-out parcial en
    // 'sin_producto': algunos productos del grupo están bien, uno quedó roto).
    setPickerSeleccion(fila.productos || []);
    setPickerBusqueda('');
    setPickerError('');
    setCrearProductoAbierto(false);
    setPickerOpen(true);
  };

  const confirmarPicker = async () => {
    if (pickerSeleccion.length === 0) { setPickerError('Elegí al menos un producto.'); return; }
    setPickerGuardando(true);
    setPickerError('');
    const idsElegidos = pickerSeleccion.map((p) => p.id_club_grido);
    try {
      if (pickerFila.estado === 'sin_mapear') {
        try {
          const res = await axios.post(API_BASE_URL + MAPEO_PRECIOS_ALTA, {
            nombre_excel: pickerFila.nombre, categoria: null, ids_club_grido: idsElegidos,
          }, { headers: authHeader() });
          if (!res.data?.success) throw new Error(res.data?.message || 'Error al mapear.');
        } catch (errAlta) {
          // Caso borde: ya existe una fila (inactiva) con ese nombre_excel —
          // reintentar como actualización en vez de alta.
          const res2 = await axios.post(API_BASE_URL + MAPEO_PRECIOS_ACTUALIZAR, {
            nombre_excel_original: pickerFila.nombre, nombre_excel: pickerFila.nombre,
            categoria: null, activo: 1, ids_club_grido: idsElegidos,
          }, { headers: authHeader() });
          if (!res2.data?.success) throw new Error(res2.data?.message || errAlta.response?.data?.message || errAlta.message || 'Error al mapear.');
        }
      } else {
        // 'sin_producto': hay que mandar la lista COMPLETA final del grupo —
        // los que ya estaban sanos (menos los rotos que se reemplazan) más
        // los recién elegidos — o se pierden los demás productos del grupo.
        const grupoExistente = grupos.find((g) => g.nombre_excel === pickerFila.nombre);
        const idsRotos = new Set((pickerFila.clubs_rotos || []).map(Number));
        const idsSanos = (pickerFila.productos || []).map((p) => p.id_club_grido).filter((id) => !idsRotos.has(Number(id)));
        const idsFinal = Array.from(new Set([...idsSanos, ...idsElegidos]));
        const res = await axios.post(API_BASE_URL + MAPEO_PRECIOS_ACTUALIZAR, {
          nombre_excel_original: pickerFila.nombre, nombre_excel: pickerFila.nombre,
          categoria: grupoExistente?.categoria || null, activo: 1, ids_club_grido: idsFinal,
        }, { headers: authHeader() });
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al mapear.');
      }
      avisarExito('Línea mapeada correctamente.');
      setPickerOpen(false);
      cargar();
      if (excelToken && excelHoja) cargarPlanilla(excelToken, excelHoja);
    } catch (e) {
      setPickerError(e.response?.data?.message || e.message || 'Error al mapear.');
    } finally {
      setPickerGuardando(false);
    }
  };

  // --- Crear producto nuevo desde el picker (no está en el catálogo) ---

  const abrirCrearProducto = () => {
    setNuevoProducto({
      descripcion: pickerFila?.nombre || '', precio: pickerFila?.precio_nuevo ?? '',
      grupo: '', id_club_grido: '',
    });
    setErrorNuevoProducto('');
    setCrearProductoAbierto(true);
    if (gruposProducto.length === 0) {
      axios.get(API_BASE_URL + PRODUCTO_FORMDATA, { headers: authHeader() })
        .then((res) => setGruposProducto(res.data?.data?.grupos || []))
        .catch(() => {});
    }
  };

  const crearProducto = async () => {
    setErrorNuevoProducto('');
    if (!nuevoProducto.descripcion.trim() || !nuevoProducto.grupo) {
      setErrorNuevoProducto('Descripción y grupo son requeridos.'); return;
    }
    if (!nuevoProducto.id_club_grido) {
      setErrorNuevoProducto('El ID Club Grido es requerido.'); return;
    }
    setCreandoProducto(true);
    try {
      const fd = new FormData();
      fd.append('art_descripcion', nuevoProducto.descripcion.trim());
      fd.append('art_precio_normal', nuevoProducto.precio || 0);
      fd.append('grupo', nuevoProducto.grupo);
      fd.append('id_club_grido', nuevoProducto.id_club_grido);
      const res = await axios.post(API_BASE_URL + PRODUCTO_ALTA, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al crear el producto.');
      const idClubNuevo = Number(nuevoProducto.id_club_grido);
      setPickerSeleccion((sel) => [...sel, {
        id_club_grido: idClubNuevo, descripcion: nuevoProducto.descripcion.trim(), precio: Number(nuevoProducto.precio) || 0,
      }]);
      setCrearProductoAbierto(false);
      avisarExito('Producto creado — ahora confirmá el mapeo.');
      cargarProductosCatalogo();
    } catch (e) {
      setErrorNuevoProducto(e.response?.data?.message || e.message || 'Error al crear el producto.');
    } finally {
      setCreandoProducto(false);
    }
  };

  const abrirNuevo = () => { setForm(FORM_VACIO); setFormInicial(FORM_VACIO); setErrorForm(''); setBusquedaProductos(''); setDialogOpen(true); };
  const abrirEditar = (g) => {
    const f = {
      nombreExcelOriginal: g.nombre_excel, nombre_excel: g.nombre_excel,
      categoria: g.categoria || '', activo: g.activo, productos: g.productos,
    };
    setForm(f);
    setFormInicial(f);
    setErrorForm('');
    setBusquedaProductos('');
    setDialogOpen(true);
  };

  // Elegir productos en el buscador da la sensación de "ya quedó guardado"
  // (aparecen los chips), pero nada se persiste hasta tocar Guardar. El botón
  // "Cancelar" ya es una decisión explícita de no guardar, así que cierra
  // directo. La confirmación es solo para cierres accidentales del diálogo
  // (click afuera o Escape), donde sí conviene avisar antes de perder todo.
  const huboCambios = () => JSON.stringify(form) !== JSON.stringify(formInicial);
  const cancelarDialogo = () => setDialogOpen(false);
  const cerrarDialogoAccidental = () => {
    // Antes usaba window.confirm() (el "alert" feo del navegador) -- ahora
    // es un Dialog propio, igual que el resto de las confirmaciones de esta
    // pantalla, para no mezclar un popup nativo con el resto de la UI.
    if (huboCambios()) { setConfirmarDescarteOpen(true); return; }
    setDialogOpen(false);
  };
  const descartarCambios = () => { setConfirmarDescarteOpen(false); setDialogOpen(false); };

  const guardar = async () => {
    setErrorForm('');
    if (!form.nombre_excel.trim()) { setErrorForm('El nombre (tal cual aparece en el Excel) es requerido.'); return; }
    const esNuevo = !form.nombreExcelOriginal;
    // Un mapeo NUEVO sin productos no tendría sentido crearlo (el alta no
    // insertaría ninguna fila). Pero uno YA EXISTENTE sí se puede guardar
    // sin productos a propósito: es la forma de "sacarle el mapeo" por
    // completo (el backend borra las filas de ese nombre_excel si la lista
    // de ids_club_grido queda vacía) -- antes esto quedaba bloqueado acá.
    if (esNuevo && form.productos.length === 0) { setErrorForm('Elegí al menos un producto.'); return; }
    setGuardando(true);
    try {
      const url = esNuevo ? MAPEO_PRECIOS_ALTA : MAPEO_PRECIOS_ACTUALIZAR;
      const payload = {
        nombre_excel: form.nombre_excel.trim(),
        categoria: form.categoria.trim(),
        activo: form.activo ? 1 : 0,
        ids_club_grido: form.productos.map((p) => p.id_club_grido),
      };
      if (!esNuevo) payload.nombre_excel_original = form.nombreExcelOriginal;
      const res = await axios.post(API_BASE_URL + url, payload, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
      avisarExito(esNuevo ? 'Mapeo creado.' : (form.productos.length === 0 ? 'Mapeo eliminado (quedó sin productos).' : 'Mapeo actualizado.'));
      setDialogOpen(false);
      cargar();
    } catch (e) {
      setErrorForm(e.response?.data?.message || e.message || 'Error al guardar.');
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (g) => {
    try {
      const res = await axios.post(API_BASE_URL + MAPEO_PRECIOS_ACTUALIZAR, {
        nombre_excel_original: g.nombre_excel, nombre_excel: g.nombre_excel,
        categoria: g.categoria || '', activo: g.activo ? 0 : 1,
        ids_club_grido: g.productos.map((p) => p.id_club_grido),
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message);
      cargar();
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al cambiar el estado.');
    }
  };

  const columnas = [
    {
      field: 'nombre_excel', headerName: 'Nombre en el Excel', flex: 1, minWidth: 180,
      renderCell: (p) => (
        <>
          {p.row.nombre_excel}
          {p.row.enExcel === false && <Chip size="small" color="error" label="no está en el Excel" sx={{ ml: 1 }} />}
        </>
      ),
    },
    {
      field: 'productos', headerName: 'Productos', flex: 1.5, minWidth: 260,
      renderCell: (p) => {
        const nombres = p.row.productos.map((x) => x.descripcion || `club ${x.id_club_grido}`);
        const huerfanos = p.row.productos.some((x) => !x.descripcion);
        return (
          <span title={nombres.join(', ')} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Chip size="small" label={p.row.productos.length} sx={{ mr: 1 }} />
            {nombres.join(', ')}
            {huerfanos && <Chip size="small" color="warning" label="huérfano" sx={{ ml: 1 }} />}
          </span>
        );
      },
    },
    { field: 'categoria', headerName: 'Categoría', width: 140 },
    {
      field: 'activo', headerName: 'Activo', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => <Switch size="small" checked={!!Number(p.row.activo)} onChange={() => toggleActivo(p.row)} onClick={(e) => e.stopPropagation()} />,
    },
    {
      field: 'actions', headerName: '', width: 60, sortable: false, filterable: false,
      renderCell: (p) => <IconButton size="small" color="primary" onClick={() => abrirEditar(p.row)}><Edit fontSize="small" /></IconButton>,
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={SyncAlt}
          titulo="Mapeo de precios"
          subtitulo={`${grupos.length} mapeo${grupos.length !== 1 ? 's' : ''}`}
          ayuda="Relaciona el nombre exacto de cada línea de la lista de precios en Excel con uno o varios productos del catálogo. Si un nombre de Excel agrupa una categoría entera (ej. 'SMOOTHIE'), elegí todos los productos correspondientes: al cambiar el precio de esa línea, se actualizan todos juntos. 'Actualizar precios' usa esta tabla para reconocer cada línea. Con 'Verificar contra un Excel' podés detectar mapeos viejos que ya no corresponden a ninguna línea de la lista actual, y con 'Limpiar duplicados' desactivar mapeos individuales viejos que quedaron duplicados dentro de un grupo más grande."
          accion={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <input ref={fileVerificarRef} type="file" accept=".xlsx" hidden onChange={onSubirParaVerificar} />
              <Button variant="outlined" startIcon={<MenuBook />} onClick={() => setGuiaAbierta(true)}>Paso a paso</Button>
              <Button
                variant="outlined" startIcon={verificando ? <CircularProgress size={16} /> : <FactCheck />}
                onClick={() => fileVerificarRef.current?.click()} disabled={verificando}
              >
                {verificando ? 'Leyendo…' : 'Verificar contra un Excel'}
              </Button>
              <Button variant="outlined" startIcon={<CleaningServices />} onClick={buscarDuplicados}>
                Limpiar duplicados
              </Button>
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={abrirNuevo}>Nuevo mapeo</Button>
            </Box>
          }
        />
        <GuiaActualizarPrecios open={guiaAbierta} onClose={() => setGuiaAbierta(false)} />
        <VolverBreadcrumb to="/precios" label="Actualizar precios" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Planilla del Excel" value={0} />
          <Tab label="Todos los mapeos" value={1} />
        </Tabs>

        {tab === 0 && (
          !excelToken ? (
            <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/precios')}>Ir a Actualizar precios</Button>}>
              Subí un Excel desde "Actualizar precios" para ver esta vista.
            </Alert>
          ) : cargandoPlanilla ? (
            <Cargando inline />
          ) : errorPlanilla ? (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => navigate('/precios')}>Volver a subirlo</Button>}>
              {errorPlanilla}
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre en el Excel</TableCell>
                    <TableCell align="right">Precio nuevo</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filasExcel.map((fila, i) => (
                    <TableRow
                      key={i}
                      sx={{ bgcolor: (t) => alpha(t.palette[fila.estado === 'mapeado' ? 'success' : 'error'].main, 0.08) }}
                    >
                      <TableCell>{fila.nombre}</TableCell>
                      <TableCell align="right">{fmtMoneda(fila.precio_nuevo)}</TableCell>
                      <TableCell>
                        {fila.estado === 'mapeado' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip size="small" color="success" label="Mapeado" />
                            <Typography variant="body2">{fila.productos.map((p) => p.descripcion).join(', ')}</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              size="small" color={fila.estado === 'sin_producto' ? 'error' : 'warning'}
                              label={fila.estado === 'sin_producto' ? 'Sin producto' : 'Sin mapear'}
                            />
                            {fila.estado === 'sin_producto' && fila.productos?.length > 0 && (
                              <Typography variant="caption" color="text.secondary">{fila.productos.map((p) => p.descripcion).join(', ')}</Typography>
                            )}
                            <Button size="small" variant="outlined" onClick={() => abrirPicker(fila)}>Elegir producto(s)</Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filasExcel.length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>Sin filas.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )
        )}

        {tab === 1 && (
          <>
            {nombresEnExcel && (
              <Alert
                severity="info" sx={{ mb: 2 }}
                action={
                  <FormControlLabel
                    sx={{ mr: 1 }}
                    control={<Switch size="small" checked={soloNoEncontrados} onChange={(e) => setSoloNoEncontrados(e.target.checked)} />}
                    label="Solo no encontrados"
                  />
                }
              >
                Verificado contra "{archivoVerificado}": {gruposConVerificacion.filter((g) => !g.enExcel).length} de {grupos.length} mapeos no corresponden a ninguna línea de este Excel.
              </Alert>
            )}

            <TextField
              fullWidth size="small" placeholder="Buscar por nombre de Excel, producto o categoría…"
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Cargando />
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {filtrados.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>
                )}
                {filtrados.map((g) => (
                  <Paper key={g.nombre_excel} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {g.nombre_excel}
                          {g.enExcel === false && <Chip size="small" color="error" label="no está en el Excel" sx={{ ml: 1 }} />}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {g.productos.map((p) => (
                            <Chip
                              key={p.id_club_grido} size="small"
                              color={p.descripcion ? 'default' : 'warning'}
                              label={p.descripcion || `Sin producto (club ${p.id_club_grido})`}
                            />
                          ))}
                        </Box>
                      </Box>
                      <IconButton size="small" color="primary" onClick={() => abrirEditar(g)}><Edit fontSize="small" /></IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Chip size="small" label={g.categoria || 'sin categoría'} variant="outlined" />
                      <Switch size="small" checked={!!Number(g.activo)} onChange={() => toggleActivo(g)} />
                    </Box>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <div style={{ height: 620, width: '100%' }}>
                <DataGrid
                  rows={filtrados} columns={columnas} pageSize={15} rowsPerPageOptions={[15, 25, 50]}
                  getRowId={(row) => row.nombre_excel} disableSelectionOnClick
                  getRowHeight={() => 'auto'}
                  components={{ Toolbar: ExportToolbar }}
                />
              </div>
            )}
          </>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !guardando && cerrarDialogoAccidental()} maxWidth="sm" fullWidth>
        <DialogTitle>{form.nombreExcelOriginal ? 'Editar mapeo' : 'Nuevo mapeo'}</DialogTitle>
        <DialogContent>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}

          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            1. Línea del Excel
          </Typography>
          <TextField
            fullWidth size="small" label="Nombre tal cual en el Excel" sx={{ mb: 0.5, mt: 0.5 }}
            value={form.nombre_excel} onChange={(e) => setForm((f) => ({ ...f, nombre_excel: e.target.value }))}
            helperText='Tiene que coincidir exactamente con la columna B de la lista de precios (ej: "SMOOTHIE").'
            autoFocus
          />

          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mt: 2.5 }}>
            2. Productos que representa esta línea
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Tildá todos los productos a los que les corresponde{form.nombre_excel ? ` el precio de "${form.nombre_excel}"` : ' este precio'}.
            Si agrupa varios sabores/variantes (ej. Smoothie Naranja, Smoothie Pera, Smoothie Ananá), elegilos todos acá.
          </Typography>

          {form.productos.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {form.productos.map((p) => (
                <Chip
                  key={p.id_club_grido} size="small"
                  color={p.descripcion ? 'default' : 'warning'}
                  label={etiquetaProducto(p)}
                  onDelete={() => quitarProducto(p)}
                />
              ))}
            </Box>
          ) : form.nombreExcelOriginal && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Si guardás sin ningún producto elegido, este mapeo se elimina.
            </Alert>
          )}
          <TextField
            fullWidth size="small" placeholder="Buscar por nombre de producto…"
            value={busquedaProductos} onChange={(e) => setBusquedaProductos(e.target.value)}
          />
          <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto', mt: 1 }}>
            <List dense disablePadding>
              {productosFiltrados.length === 0 ? (
                <ListItem><ListItemText secondary="Sin resultados." /></ListItem>
              ) : productosFiltrados.map((p) => {
                const elegido = form.productos.some((x) => x.id_club_grido === p.id_club_grido);
                return (
                  <ListItemButton key={p.id_club_grido} onClick={() => toggleProducto(p)} dense selected={elegido}>
                    <Checkbox edge="start" checked={elegido} tabIndex={-1} disableRipple size="small" sx={{ mr: 1 }} />
                    <ListItemText primary={etiquetaProducto(p)} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Click para sumar o sacar un producto, sin que la lista se cierre.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField
              size="small" label="Categoría (opcional)" sx={{ flex: '1 1 200px' }}
              value={form.categoria} onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
              helperText="Solo para agrupar en pantalla (ej: cucuruchos, batidos)."
            />
            {form.nombreExcelOriginal && (
              <FormControlLabel
                control={<Switch checked={!!form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked ? 1 : 0 }))} />}
                label="Activo"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarDialogo} disabled={guardando}>Cancelar</Button>
          <Button
            variant="contained" onClick={guardar} disabled={guardando}
            color={form.nombreExcelOriginal && form.productos.length === 0 ? 'error' : 'primary'}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {guardando ? 'Guardando…' : (form.nombreExcelOriginal && form.productos.length === 0 ? 'Eliminar mapeo' : 'Guardar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmarDescarteOpen} onClose={() => setConfirmarDescarteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Descartar los cambios?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Todavía no guardaste los cambios de este mapeo — si cerrás ahora, se pierden.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarDescarteOpen(false)}>Seguir editando</Button>
          <Button variant="contained" color="error" onClick={descartarCambios}>Descartar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicadosOpen} onClose={() => !limpiando && setDuplicadosOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Limpiar mapeos duplicados</DialogTitle>
        <DialogContent>
          {buscandoDuplicados ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : !duplicados || duplicados.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No se encontró ningún producto activo en más de un mapeo a la vez. No hay nada para limpiar.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Estos {duplicados.length} producto{duplicados.length !== 1 ? 's' : ''} están activos en más de un mapeo — se va a desactivar el mapeo más chico y dejar activo el más grande:
              </Typography>
              <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                {duplicados.map((c) => (
                  <ListItem key={c.id_club_grido} sx={{ display: 'block', py: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.descripcion || `club ${c.id_club_grido}`}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Queda activo en "{c.gana}" ({c.gana_cantidad} producto{c.gana_cantidad !== 1 ? 's' : ''}) — se desactiva en: {c.pierde.map((n) => `"${n}"`).join(', ')}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicadosOpen(false)} disabled={limpiando}>Cancelar</Button>
          <Button
            variant="contained" color="primary" onClick={limpiarDuplicados}
            disabled={limpiando || buscandoDuplicados || !duplicados || duplicados.length === 0}
            startIcon={limpiando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {limpiando ? 'Limpiando…' : `Desactivar ${duplicados?.length || 0}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickerOpen} onClose={() => !pickerGuardando && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mapear "{pickerFila?.nombre}"</DialogTitle>
        <DialogContent>
          {pickerError && <Alert severity="error" sx={{ mb: 2 }}>{pickerError}</Alert>}

          {pickerSeleccion.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {pickerSeleccion.map((p) => (
                <Chip
                  key={p.id_club_grido} size="small"
                  color={p.descripcion ? 'default' : 'warning'}
                  label={etiquetaProducto(p)}
                  onDelete={() => setPickerSeleccion((sel) => sel.filter((x) => x.id_club_grido !== p.id_club_grido))}
                />
              ))}
            </Box>
          )}
          <TextField
            fullWidth size="small" placeholder="Buscar por nombre de producto…"
            value={pickerBusqueda} onChange={(e) => setPickerBusqueda(e.target.value)}
          />
          <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto', mt: 1 }}>
            <List dense disablePadding>
              {pickerFiltrados.length === 0 ? (
                <ListItem><ListItemText secondary="Sin resultados." /></ListItem>
              ) : pickerFiltrados.map((p) => {
                const elegido = pickerSeleccion.some((x) => x.id_club_grido === p.id_club_grido);
                return (
                  <ListItemButton key={p.id_club_grido} onClick={() => togglePickerProducto(p)} dense selected={elegido}>
                    <Checkbox edge="start" checked={elegido} tabIndex={-1} disableRipple size="small" sx={{ mr: 1 }} />
                    <ListItemText primary={etiquetaProducto(p)} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {!crearProductoAbierto ? (
            <Button size="small" sx={{ mt: 1.5 }} onClick={abrirCrearProducto}>¿No está en el catálogo? Crear producto nuevo</Button>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Nuevo producto</Typography>
              {errorNuevoProducto && <Alert severity="error" sx={{ mb: 1 }}>{errorNuevoProducto}</Alert>}
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth size="small" label="Descripción *" value={nuevoProducto.descripcion}
                    onChange={(e) => setNuevoProducto((f) => ({ ...f, descripcion: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth size="small" type="number" label="Precio" value={nuevoProducto.precio}
                    onChange={(e) => setNuevoProducto((f) => ({ ...f, precio: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth size="small" type="number" label="ID Club Grido *" value={nuevoProducto.id_club_grido}
                    onChange={(e) => setNuevoProducto((f) => ({ ...f, id_club_grido: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Grupo *</InputLabel>
                    <Select label="Grupo *" value={nuevoProducto.grupo} onChange={(e) => setNuevoProducto((f) => ({ ...f, grupo: e.target.value }))}>
                      {gruposProducto.map((g) => <MenuItem key={g.grupo} value={g.grupo}>{g.grudescrip}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                <Button size="small" onClick={() => setCrearProductoAbierto(false)} disabled={creandoProducto}>Cancelar</Button>
                <Button
                  size="small" variant="contained" onClick={crearProducto} disabled={creandoProducto}
                  startIcon={creandoProducto ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {creandoProducto ? 'Creando…' : 'Crear y sumar'}
                </Button>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={pickerGuardando}>Cancelar</Button>
          <Button
            variant="contained" onClick={confirmarPicker} disabled={pickerGuardando || pickerSeleccion.length === 0}
            startIcon={pickerGuardando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {pickerGuardando ? 'Guardando…' : 'Mapear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MapeoPrecios;
